/**
 * Everything the Reporting page reads, in one place.
 *
 * Six reads feed the page: saved reports, generated files, schedules, the
 * delivery ledger, the sender defaults and the scheduler's switches. Each can
 * fail on its own, and the page must say which one did rather than render as
 * though the missing data were empty. The query keys are the ones the rest of
 * the app already invalidates (the pipeline schedule modal, Report Studio), so a
 * change made elsewhere still refreshes this page.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { canApproveModule, canEditModule, canExportModule } from '@/auth/modulePermission'
import { reportDefinitionsService } from '@/services/reportDefinitions'
import { reportingCenterService } from '@/services/reportingCenter'
import { reportSchedulesService } from '@/services/reportSchedules'
import { useSchedulerStatus } from '../schedulerStatus'
import { summarizeAttention } from './attention'
import { deliveryRows } from './deliveries'
import { buildLibrary } from './library'

export const HISTORY_LIMIT = 200
export const DELIVERY_LIMIT = 150

/**
 * How often the page reads schedules, files and deliveries again while it is open. The health clock
 * advances every minute; without a re-read it would compare the clock with a next run that has already
 * happened and call a schedule that ran on time overdue.
 */
export const REFRESH_MS = 60_000

export type ReportPermissions = {
  /** Run a report and take the file. */
  canRun: boolean
  /** Create, change, schedule, subscribe, share, delete. */
  canEdit: boolean
  /** Activate a schedule. */
  canApprove: boolean
}

export function useReportPermissions(): ReportPermissions {
  const { localUser } = useLocalAuth()
  return {
    canRun: canExportModule(localUser, 'reports'),
    canEdit: canEditModule(localUser, 'reports'),
    canApprove: canApproveModule(localUser, 'reports'),
  }
}

export type DataFailure = { key: string; what: string; retry: () => void }

export function useReportingData() {
  const deals = useAntarcticaDeals('reports')
  const { localUser } = useLocalAuth()
  const me = localUser?.email ?? null

  // "Overdue" and "tomorrow" move with the clock; a page left open overnight
  // must not keep reading yesterday's.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const definitionsQuery = useQuery({
    queryKey: ['report-definitions'],
    queryFn: () => reportDefinitionsService.list(),
    staleTime: 15_000,
  })
  const historyQuery = useQuery({
    queryKey: ['report-history', HISTORY_LIMIT],
    queryFn: () => reportingCenterService.listHistory(HISTORY_LIMIT),
    staleTime: 15_000,
    refetchInterval: REFRESH_MS,
  })
  const schedulesQuery = useQuery({
    queryKey: ['report-schedules'],
    queryFn: () => reportSchedulesService.list(),
    staleTime: 15_000,
    refetchInterval: REFRESH_MS,
  })
  const deliveryQuery = useQuery({
    queryKey: ['report-delivery-history', DELIVERY_LIMIT],
    queryFn: () => reportSchedulesService.getDeliveryHistory(DELIVERY_LIMIT),
    staleTime: 15_000,
    refetchInterval: REFRESH_MS,
  })
  const emailDefaultsQuery = useQuery({
    queryKey: ['report-email-defaults'],
    queryFn: () => reportSchedulesService.getEmailDefaults(),
    staleTime: 60_000,
  })
  const statusQuery = useSchedulerStatus()

  const definitions = definitionsQuery.data
  const history = historyQuery.data
  const schedules = schedulesQuery.data
  const delivery = deliveryQuery.data
  const status = statusQuery.data

  const library = useMemo(
    () =>
      buildLibrary({
        definitions: definitions ?? [],
        schedules: schedules ?? [],
        history: history ?? [],
        deliveries: delivery?.items ?? [],
        deals,
        me,
        status,
        now,
      }),
    [definitions, schedules, history, delivery?.items, deals, me, status, now],
  )

  const attention = useMemo(
    () =>
      summarizeAttention({
        schedules: schedules ?? [],
        deliveries: delivery?.items ?? [],
        status,
        now,
        deliveryRunLimit: DELIVERY_LIMIT,
      }),
    [schedules, delivery?.items, status, now],
  )

  const deliveries = useMemo(() => deliveryRows(delivery, schedules ?? [], now), [delivery, schedules, now])

  const loading = definitionsQuery.isLoading || historyQuery.isLoading || schedulesQuery.isLoading

  /** Which reads failed, by plain name, so the page can say what it is missing. */
  const failures: DataFailure[] = []
  if (definitionsQuery.isError)
    failures.push({ key: 'definitions', what: 'saved reports', retry: () => void definitionsQuery.refetch() })
  if (historyQuery.isError)
    failures.push({ key: 'history', what: 'generated files', retry: () => void historyQuery.refetch() })
  if (schedulesQuery.isError)
    failures.push({ key: 'schedules', what: 'schedules', retry: () => void schedulesQuery.refetch() })
  if (deliveryQuery.isError)
    failures.push({ key: 'deliveries', what: 'the delivery ledger', retry: () => void deliveryQuery.refetch() })

  return {
    me,
    now,
    deals,
    definitions: definitions ?? [],
    history: history ?? [],
    schedules: schedules ?? [],
    delivery,
    deliveries,
    status,
    statusFailed: statusQuery.isError,
    senderEmail: emailDefaultsQuery.data?.senderEmail ?? null,
    library,
    attention,
    loading,
    failures,
    /** The generated-file read stopped at its limit, so older files exist that are not listed. */
    historyTruncated: (history?.length ?? 0) >= HISTORY_LIMIT,
    deliveryLoading: deliveryQuery.isLoading,
    deliveryError: deliveryQuery.isError,
    retryDeliveries: () => void deliveryQuery.refetch(),
  }
}

export type ReportingData = ReturnType<typeof useReportingData>
